import { computed } from 'vue';
import { defineStore } from 'pinia';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import { usePlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel';
import { useActivePresetTracker } from '@/views/3d-planner/composables/useActivePresetTracker';
import { catstackPlaneIds, type CatstackPlaneId } from '@/domain/anatomy/catstackPlane';
import { nextSliceIndex } from '@/util/ctSliderWheel';

const PLANE_LABELS: Record<CatstackPlaneId, string> = {
    axial: 'CT Axial',
    coronal: 'CT Coronal',
    sagittal: 'CT Sagittal',
};

/** Per-plane slider state the View binds to via `vm.planeStates[planeId]`. */
type PlaneState = {
    label: string;
    sliderValue: number;
    displayIndex: number;
    max: number;
    visible: boolean;
};

export const useViewControlsClusterViewModel = defineStore('view-controls-cluster', () => {
    const planner = usePlannerStore();
    const scene = usePlannerSceneViewModel();
    const presetTracker = useActivePresetTracker(planner, scene);

    // CT slider row open/closed
    const rowOpen = computed({
        get: () => scene.catstacks.rowOpen,
        set: (v: boolean) => {
            scene.catstacks.rowOpen = v;
        },
    });

    // Master toggle flips rowOpen only — per-plane visibility persists across
    // close/reopen within the session. Initial per-plane visibility is set in
    // catstacksState.ts, and the scene's render-gate is `rowOpen && visible`
    // (catstackImage.ts), so the planes hide from view when the row closes
    // regardless of their per-plane state.
    function toggleRowOpen() {
        rowOpen.value = !rowOpen.value;
    }

    function maxSliceFor(planeId: CatstackPlaneId): number {
        return planner.catstacksData
            ? planner.catstacksData[planeId].catstack.count - 1
            : 0;
    }

    // Per-plane slider state, keyed by planeId so the View can look up
    // its plane in one indexing step. Returning plain values inside a
    // top-level computed (rather than a function returning refs) is what
    // lets the View read `vm.planeStates[planeId].sliderValue` without
    // `.value` — Pinia unwraps the computed, then the indexing is JS
    // property access on plain primitives.
    //
    // `sliderValue` is the inverted slice position (top of slider = first
    // slice — the existing UX); `displayIndex` is the 1-based human-readable
    // slice number shown in the thumb label.
    const planeStates = computed<Record<CatstackPlaneId, PlaneState>>(() => {
        const states = {} as Record<CatstackPlaneId, PlaneState>;
        for (const planeId of catstackPlaneIds) {
            const max = maxSliceFor(planeId);
            const sliceIndex = scene.catstacks[planeId].sliceIndex;
            states[planeId] = {
                label: PLANE_LABELS[planeId],
                sliderValue: max - sliceIndex,
                displayIndex: sliceIndex + 1,
                max,
                visible: scene.catstacks[planeId].visible,
            };
        }
        return states;
    });

    function setSliderValue(planeId: CatstackPlaneId, value: number) {
        scene.catstacks[planeId].sliceIndex = maxSliceFor(planeId) - value;
    }

    function toggleVisible(planeId: CatstackPlaneId) {
        scene.catstacks[planeId].visible = !scene.catstacks[planeId].visible;
    }

    function advanceSlice(planeId: CatstackPlaneId, deltaY: number) {
        if (!scene.catstacks[planeId].visible) return;
        scene.catstacks[planeId].sliceIndex = nextSliceIndex(
            deltaY,
            scene.catstacks[planeId].sliceIndex,
            maxSliceFor(planeId),
        );
    }

    // Implant-outlines is a ModeToggleId on `planner.visibility` (same shape
    // as `xray` and cross-sections). Reads come straight off the map; writes
    // go through the existing `toggleVisibility` action — same path every
    // other visibility toggle uses.
    const showImplantOutline = computed({
        get: () => scene.visibility['implant-outlines'] === 'on',
        set: (v: boolean) => {
            scene.toggleVisibility('implant-outlines', v);
        },
    });

    // The toggle is available in stem/cup mode (initial map sets
    // 'implant-outlines' to 'on' there; 'hidden' everywhere else), so
    // visibility-of-the-toggle reduces to "is this id not currently hidden?".
    const showOutlinesVisible = computed(
        () => scene.visibility['implant-outlines'] !== 'hidden',
    );

    // presetTracker.stop is intentionally not exposed: the ViewModel is a Pinia
    // singleton whose watchers live for the Pinia instance's lifetime. Tests reset
    // this via setActivePinia(createPinia()) — no per-component teardown needed.
    return {
        rowOpen,
        activePreset: presetTracker.activePreset,
        showImplantOutline,
        showOutlinesVisible,
        planeStates,
        toggleRowOpen,
        setSliderValue,
        toggleVisible,
        advanceSlice,
        setPreset: presetTracker.setPreset,
    };
});
